import React, { useCallback } from 'react'

import Badge from '@amzn/meridian/badge'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import bellTokens from '@amzn/meridian-tokens/base/icon/bell'

import { CountMetric, EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'

import { NO_CURRENT_STORE } from '@src/constants'

import './NotificationIcon.scss'

interface INotificationIconProps {
    notificationCount?: number
    /** When set, a count above it renders as "<maxValue>+" (Meridian Badge `maxValue`). */
    maxValue?: number
    storeId?: string
    /**
     * Whether the badge needs a valid `storeId` to render (default true). The legacy count is
     * per store, so without one there is nothing to show; the Action Center count is keyed on
     * the user across every store and must not be hidden while the store list is still loading
     * or when the user has no stores at all.
     */
    requireStore?: boolean
    toggleNotificationsSheet?: () => void
}
// TODO: Add logic to support showing/hiding notification icon
export const shouldShowNotificationIcon = (): boolean => true

/**
 * Displays notification icon along with the number of notifications.
 * Clicking the icon toggles the notifications sheet.
 *
 * @param {number} [notificationCount] - Count of notifications to be displayed
 * @param {number} [maxValue] - Largest count shown as a number; anything above renders as "<maxValue>+"
 * @param {string} [storeId] - Current store ID
 * @param {boolean} [requireStore=true] - Hide the badge unless storeId is a real store (legacy per-store count)
 * @param {() => void} [toggleNotificationsSheet] - Callback to toggle notifications sheet
 * @returns {React.ReactElement} Notification icon button or empty fragment
 */
const NotificationIcon = ({ notificationCount, maxValue, storeId, requireStore = true, toggleNotificationsSheet }: Readonly<INotificationIconProps>): React.ReactElement => {
    if (!shouldShowNotificationIcon()) {
        return <></>
    }

    const handleNotificationClick = useCallback(() => {
        new CountMetric(EnvironmentDetail.getStoreId(), 'OpenNotificationModal', 'IhmFlyingDutchman', 'NotificationIcon').count(1)

        if (toggleNotificationsSheet) {
            toggleNotificationsSheet()
        }
    }, [toggleNotificationsSheet])

    const storeGateOk = !requireStore || (!!storeId && storeId !== NO_CURRENT_STORE)
    const isActive = notificationCount > 0 && storeGateOk

    return (
        <Button type="icon" id="notificationButton" data-testid="NotificationIcon" onClick={handleNotificationClick}>
            <Icon tokens={bellTokens}>
                {isActive && <Badge type="error" value={notificationCount} maxValue={maxValue} data-testid="notification-badge" />}
            </Icon>
        </Button>
    )
}

export default NotificationIcon
